import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Pencil, DollarSign } from "lucide-react";
import { apiBaseUrl, authHeaders, formatHm } from "./shared";
import DriverCalendar from "./DriverCalendar";

const PAY_TYPES = [
  { value: "per_mile", label: "Per mile", field: "pay_rate_per_mile", unit: "$/mi", step: "0.01" },
  { value: "hourly", label: "Hourly", field: "pay_rate_hourly", unit: "$/hr", step: "0.5" },
  { value: "fixed_daily", label: "Fixed daily", field: "pay_rate_fixed_daily", unit: "$/day", step: "1" },
];

function payTypeMeta(payType) {
  return PAY_TYPES.find((p) => p.value === payType) || PAY_TYPES[0];
}

function formatPay(driver) {
  const meta = payTypeMeta(driver.pay_type);
  const value = driver[meta.field];
  if (value === null || value === undefined || value === "") return "Not set";
  return `$${Number(value).toFixed(2)} ${meta.unit}`;
}

export default function DriversPage() {
  const [view, setView] = useState("table");
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Editing (phone + pay rate) is open to any dispatcher, not just admin --
  // the backend ACL already grants the dispatcher role full GET+PUT on
  // drivers, so this only used to be a client-side gate with no matching
  // server-side restriction behind it.
  const canEdit = true;

  const [editingId, setEditingId] = useState(null);
  const [editPayType, setEditPayType] = useState("per_mile");
  const [editRate, setEditRate] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${apiBaseUrl}/drivers/?limit=1000`, authHeaders())
      .then((res) => setDrivers(res.data.records || res.data || []))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function startEdit(driver) {
    setEditingId(driver.id);
    setSaveError(null);
    const meta = payTypeMeta(driver.pay_type);
    setEditPayType(meta.value);
    setEditRate(driver[meta.field] != null ? String(driver[meta.field]) : "");
    setEditPhone(driver.phone || "");
  }

  function cancelEdit() {
    setEditingId(null);
    setSaveError(null);
  }

  function changePayType(newType) {
    setEditPayType(newType);
    setEditRate("");
  }

  function saveDriver(driver) {
    const rateNum = parseFloat(editRate);
    if (Number.isNaN(rateNum) || rateNum < 0) {
      setSaveError("Enter a valid, non-negative rate.");
      return;
    }
    const meta = payTypeMeta(editPayType);
    setSaving(true);
    setSaveError(null);
    axios
      .put(
        `${apiBaseUrl}/drivers/${driver.id}`,
        { pay_type: editPayType, [meta.field]: rateNum, phone: editPhone.trim() || null },
        authHeaders()
      )
      .then(() => {
        setEditingId(null);
        load();
      })
      .catch((err) => setSaveError(err.response?.data?.detail || err.message))
      .finally(() => setSaving(false));
  }

  const editMeta = payTypeMeta(editPayType);

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Drivers</h1>
        <div style={styles.subtitle}>
          Synced from Motive, plus dispatcher-set availability overrides.
          Dispatchers can also set each driver's phone number and pay rate here -- pay rate is used by the Cost Engine on every quote.
        </div>
      </div>

      <div style={styles.tabs}>
        <button style={{ ...styles.tab, ...(view === "table" ? styles.tabActive : {}) }} onClick={() => setView("table")}>
          Table
        </button>
        <button style={{ ...styles.tab, ...(view === "calendar" ? styles.tabActive : {}) }} onClick={() => setView("calendar")}>
          Calendar
        </button>
      </div>

      {view === "calendar" ? (
        <DriverCalendar />
      ) : (
        <div style={styles.card}>
          {error && <div style={styles.errorBox}>Couldn't load drivers: {error}</div>}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>HOS remaining</th>
                  <th style={styles.th}>Cycle remaining</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Pay rate</th>
                  {canEdit && <th style={styles.th}></th>}
                </tr>
              </thead>
              <tbody>
                {drivers.map((d) =>
                  editingId === d.id ? (
                    <tr key={d.id}>
                      <td style={styles.td} colSpan={canEdit ? 7 : 6}>
                        <div style={styles.editRow}>
                          <div style={styles.editDriverName}>{d.driver_name}</div>
                          <input
                            type="tel"
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            style={styles.phoneInput}
                            placeholder="Phone number"
                            disabled={saving}
                          />
                          <select
                            style={styles.select}
                            value={editPayType}
                            onChange={(e) => changePayType(e.target.value)}
                            disabled={saving}
                          >
                            {PAY_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                          <div style={styles.rateInputWrap}>
                            <DollarSign size={13} style={{ color: "var(--muted)" }} />
                            <input
                              type="number"
                              step={editMeta.step}
                              min="0"
                              value={editRate}
                              onChange={(e) => setEditRate(e.target.value)}
                              style={styles.rateInput}
                              placeholder="0.00"
                              disabled={saving}
                            />
                            <span style={styles.rateUnit}>{editMeta.unit}</span>
                          </div>
                          <button style={styles.saveBtn} onClick={() => saveDriver(d)} disabled={saving}>
                            {saving ? "Saving…" : "Save"}
                          </button>
                          <button style={styles.cancelBtn} onClick={cancelEdit} disabled={saving}>
                            Cancel
                          </button>
                        </div>
                        {saveError && <div style={styles.saveError}>{saveError}</div>}
                      </td>
                    </tr>
                  ) : (
                    <tr key={d.id}>
                      <td style={styles.td}>{d.driver_name}</td>
                      <td style={styles.td}>{d.phone || "—"}</td>
                      <td style={styles.td}>{d.hos_hours_remaining != null ? formatHm(d.hos_hours_remaining) : "—"}</td>
                      <td style={styles.td}>{d.hos_cycle_hours_remaining != null ? formatHm(d.hos_cycle_hours_remaining) : "—"}</td>
                      <td style={styles.td}>{d.status || "—"}</td>
                      <td style={styles.td}>{formatPay(d)}</td>
                      {canEdit && (
                        <td style={styles.td}>
                          <button style={styles.editBtn} onClick={() => startEdit(d)} title="Edit phone / pay rate">
                            <Pencil size={12} /> Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                )}
                {!loading && !error && drivers.length === 0 && (
                  <tr><td style={styles.td} colSpan={canEdit ? 7 : 6}>No drivers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 640, lineHeight: 1.5 },
  tabs: { display: "flex", gap: 6, marginBottom: 16 },
  tab: {
    fontSize: 12.5, fontWeight: 600, color: "var(--muted)", background: "var(--card)",
    border: "1px solid var(--line)", borderRadius: 8, padding: "7px 16px", cursor: "pointer",
  },
  tabActive: { color: "#fff", background: "var(--brand)", borderColor: "var(--brand)" },

  errorBox: {
    background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626",
    borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16,
  },
  card: {
    background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)",
    padding: 18, boxShadow: "var(--shadow-xs)",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", padding: "8px 10px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  td: { padding: "8px 10px", color: "var(--ink-2)", borderBottom: "1px solid var(--line)" },

  editBtn: {
    display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, color: "var(--ink-2)",
    background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8, padding: "5px 10px", cursor: "pointer",
  },
  editRow: { display: "flex", alignItems: "center", gap: 10, padding: "6px 0", flexWrap: "wrap" },
  editDriverName: { fontSize: 13, fontWeight: 700, color: "var(--ink)", marginRight: 8 },
  select: {
    fontSize: 12.5, color: "var(--ink)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "6px 10px",
  },
  phoneInput: {
    fontSize: 12.5, color: "var(--ink)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "6px 10px", width: 130,
  },
  rateInputWrap: {
    display: "flex", alignItems: "center", gap: 5, background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "4px 10px",
  },
  rateInput: { border: "none", background: "transparent", width: 80, fontSize: 12.5, color: "var(--ink)", outline: "none" },
  rateUnit: { fontSize: 11.5, color: "var(--muted)", fontWeight: 600 },
  saveBtn: {
    fontSize: 12.5, fontWeight: 700, color: "#fff", background: "var(--brand)", border: "none",
    borderRadius: 8, padding: "7px 14px", cursor: "pointer",
  },
  cancelBtn: {
    fontSize: 12.5, fontWeight: 600, color: "var(--ink-2)", background: "var(--bg)", border: "1px solid var(--line)",
    borderRadius: 8, padding: "7px 14px", cursor: "pointer",
  },
  saveError: { color: "#dc2626", fontSize: 12, marginTop: 6 },
};
